'use client';

import { useState } from 'react';
import { AdminProfile, Client } from '../../types';
import { MOCK_CLIENTS } from '../../data/mock-data';
import AdminHeader from '../shared/AdminHeader';
import AdminNav from '../shared/AdminNav';

interface ClientsPageProps {
  profile: AdminProfile;
  onNavigate: (page: 'dashboard' | 'receipts' | 'clients' | 'users' | 'settings') => void;
  onLogout: () => void;
  onGoToKiosk: () => void;
}

export default function ClientsPage({
  profile,
  onNavigate,
  onLogout,
  onGoToKiosk,
}: ClientsPageProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert(`Client ID가 클립보드에 복사되었습니다:\n${text}`);
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      suspended: 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = {
      active: '활성',
      inactive: '비활성',
      suspended: '정지',
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[status] || colors.inactive}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getLastSeenStatus = (lastSeen: string | null) => {
    if (!lastSeen) return { text: '접속 기록 없음', color: 'text-gray-500' };

    const now = new Date().getTime();
    const lastSeenTime = new Date(lastSeen).getTime();
    const diff = now - lastSeenTime;
    const minutes = Math.floor(diff / 1000 / 60);

    if (minutes < 10) return { text: '온라인', color: 'text-green-600' };
    if (minutes < 60) return { text: `${minutes}분 전`, color: 'text-yellow-600' };
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return { text: `${hours}시간 전`, color: 'text-orange-600' };
    const days = Math.floor(hours / 24);
    return { text: `${days}일 전`, color: 'text-red-600' };
  };

  const filteredClients = MOCK_CLIENTS.filter((client) => {
    const matchesSearch =
      client.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.location?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader profile={profile} onLogout={onLogout} onGoToKiosk={onGoToKiosk} />
      <AdminNav
        currentPage="clients"
        onNavigate={onNavigate}
        isSuperAdmin={profile.role === 'super_admin'}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header with Create Button */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">업체(Client) 관리</h2>
            <p className="text-sm text-gray-600 mt-1">
              키오스크 설치 업체를 관리하고 Client ID를 발급합니다
            </p>
          </div>
          <button
            onClick={() => alert('새 업체 등록 (데모)')}
            className="px-6 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-semibold"
          >
            + 새 업체 등록
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-6 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                검색 (Client ID / 회사명 / 지점)
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="검색어 입력"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                상태
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">전체</option>
                <option value="active">활성</option>
                <option value="inactive">비활성</option>
                <option value="suspended">정지</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-sm text-blue-800">
            총 <strong>{filteredClients.length}</strong>개의 업체가 검색되었습니다.
          </p>
        </div>

        {/* Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  회사명
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  지점
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  담당자
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  연락처
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  상태
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  최근 접속
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredClients.map((client) => {
                const lastSeenStatus = getLastSeenStatus(client.last_seen_at);
                return (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                          {client.id}
                        </code>
                        <button
                          onClick={() => copyToClipboard(client.id)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="복사"
                        >
                          📋
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {client.company_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {client.location || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {client.contact_person || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {client.contact_phone || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(client.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={lastSeenStatus.color}>{lastSeenStatus.text}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <button
                        onClick={() => alert(`수정: ${client.id} (데모)`)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => alert(`전표 보기: ${client.id} (데모)`)}
                        className="text-green-600 hover:text-green-900"
                      >
                        전표
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filteredClients.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                    검색 결과가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
